import Axios, { VirtualDeviceApi } from '@/api';
import { DeviceType } from '@/common/constants';
import { fetchPhysicalMappingItems } from '@/common/utils/buttonMappingMqtt';

const PHYSICAL_MAPPING_TYPE = 1;
const HOST_OP_TTL_MS = 12000;

/** @type {Map<string, { onoff: number, bright: number, color: number, ts: number }>} */
const recentHostOps = new Map();
/** @type {Map<string, { onoff: number, bright: number, color: number, ts: number }>} */
const recentDirectMemberOps = new Map();
/** @type {Map<string, string>} memberDeviceId -> hostDeviceId */
const memberToHostId = new Map();
/** @type {Map<string, string[]>} hostDeviceId -> memberDeviceIds */
const hostToMemberIds = new Map();
/** @type {Map<string, boolean>} memberDeviceId -> 마스터 ON 시 앱에 켜짐 표시 */
const memberShowState = new Map();

export function recordPhysicalMappingHostOp(hostDeviceId, onoff, bright, color) {
  if (hostDeviceId == null) return;
  recentHostOps.set(String(hostDeviceId), {
    onoff: Number(onoff) === 1 ? 1 : 0,
    bright: bright != null ? Number(bright) : 0,
    color: color != null ? Number(color) : 0,
    ts: Date.now(),
  });
}

export function recordDirectMemberControl(memberDeviceId, onoff, bright, color) {
  if (memberDeviceId == null) return;
  recentDirectMemberOps.set(String(memberDeviceId), {
    onoff: Number(onoff) === 1 ? 1 : 0,
    bright: bright != null ? Number(bright) : 0,
    color: color != null ? Number(color) : 0,
    ts: Date.now(),
  });
}

export function echoMatchesRecentDirectMemberOp(memberDeviceId, onoff, bright, color) {
  const op = recentDirectMemberOps.get(String(memberDeviceId));
  if (!op || Date.now() - op.ts > HOST_OP_TTL_MS) {
    if (op) recentDirectMemberOps.delete(String(memberDeviceId));
    return false;
  }
  if (Number(onoff) !== Number(op.onoff)) return false;
  if (Number(bright) === 0 && Number(color) === 0) return true;
  return Number(bright) === Number(op.bright) && Number(color) === Number(op.color);
}

export function echoMatchesRecentHostOp(hostDeviceId, onoff, bright, color) {
  const op = recentHostOps.get(String(hostDeviceId));
  if (!op || Date.now() - op.ts > HOST_OP_TTL_MS) {
    if (op) recentHostOps.delete(String(hostDeviceId));
    return false;
  }
  if (Number(onoff) !== Number(op.onoff)) return false;
  if (Number(bright) === 0 && Number(color) === 0) return true;
  return Number(bright) === Number(op.bright) && Number(color) === Number(op.color);
}

export function clearDirectMemberOps(deviceIds) {
  for (const id of deviceIds || []) {
    if (id != null) recentDirectMemberOps.delete(String(id));
  }
}

export function rememberMappingGroup(hostDeviceId, memberDeviceIds, showStateById = null) {
  if (hostDeviceId == null) return;
  const hostKey = String(hostDeviceId);
  const prevMembers = hostToMemberIds.get(hostKey) || [];
  for (const mid of prevMembers) {
    if (memberToHostId.get(mid) === hostKey) {
      memberToHostId.delete(mid);
      memberShowState.delete(mid);
    }
  }
  const ids = (memberDeviceIds || []).filter((id) => id != null).map((id) => String(id));
  hostToMemberIds.set(hostKey, ids);
  for (const mid of ids) {
    memberToHostId.set(mid, hostKey);
    memberShowState.set(mid, showStateById?.get(mid) === true);
  }
}

export function memberShowsHostState(memberDeviceId) {
  if (memberDeviceId == null) return false;
  return memberShowState.get(String(memberDeviceId)) === true;
}

export function getCachedMappingHostId(memberDeviceId) {
  if (memberDeviceId == null) return null;
  return memberToHostId.get(String(memberDeviceId)) ?? null;
}

/** origin.requester === physicalMappingHost → skip (hostDeviceId / hostSerial 무관) */
export function isPhysicalMappingHostOrigin(payload) {
  return payload?.origin?.requester === 'physicalMappingHost';
}

/**
 * 멤버 echo를 host fan-out으로 skip 할지 판단.
 * 1) direct member op 일치 → skip 안 함 (직접 조작 우선)
 * 2) origin.physicalMappingHost → skip (hostSerial/hostDeviceId 모두 허용)
 * 3) recent host op → skip (cross-fan-out)
 */
export function shouldSkipMemberHostEcho(device, hostId, onoff, bright, color, { fromMappingHost = false } = {}) {
  if (device?._id != null && echoMatchesRecentDirectMemberOp(device._id, onoff, bright, color)) {
    return false;
  }

  if (fromMappingHost) {
    return true;
  }

  const resolvedHostId = hostId ?? getCachedMappingHostId(device?._id);
  if (!resolvedHostId || String(resolvedHostId) === String(device?._id)) {
    return false;
  }

  return echoMatchesRecentHostOp(resolvedHostId, onoff, bright, color);
}

export async function isPhysicalMappingHostDevice(deviceId) {
  if (deviceId == null) return false;
  try {
    const result = await Axios.get(
      VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', deviceId),
      {},
      '',
      { hideLoading: true },
    );
    const isHost = result?.resultCode === '0000'
      && Number(result.resultData?.mappingType) === PHYSICAL_MAPPING_TYPE;
    if (isHost && result.resultData?.items != null) {
      const items = Array.isArray(result.resultData.items)
        ? result.resultData.items
        : Object.values(result.resultData.items || {});
      const lamps = items.filter((it) => Number(it?.type) === DeviceType.Lamp && it._id != null);
      const memberIds = lamps.map((it) => it._id);
      const showStateById = new Map(lamps.map((it) => [String(it._id), it.showState === true]));
      rememberMappingGroup(deviceId, memberIds, showStateById);
    }
    return isHost;
  } catch {
    return false;
  }
}

/** 다른 조명이 포함된 물리 매핑 버튼인지. 이름 옆 (매핑버튼) 표시용. */
export async function hasPhysicalMappingMembers(deviceId) {
  if (deviceId == null) return false;
  try {
    const result = await Axios.get(
      VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', deviceId),
      {},
      '',
      { hideLoading: true },
    );
    if (result?.resultCode !== '0000') return false;
    if (Number(result.resultData?.mappingType) !== PHYSICAL_MAPPING_TYPE) return false;
    const items = Array.isArray(result.resultData?.items)
      ? result.resultData.items
      : Object.values(result.resultData?.items || {});
    return items.some(
      (it) => Number(it?.type) === DeviceType.Lamp
        && it?._id != null
        && String(it._id) !== String(deviceId),
    );
  } catch {
    return false;
  }
}

async function fetchMappingHostIdFromApi(memberDeviceId) {
  if (memberDeviceId == null) return null;
  try {
    const result = await Axios.get(
      VirtualDeviceApi.VIRTUAL_DEVICES_BY_MEMBERS,
      { deviceIds: String(memberDeviceId), mappingType: PHYSICAL_MAPPING_TYPE },
      '',
      { hideLoading: true },
    );
    const hosts = result?.resultData || [];
    return hosts.length > 0 ? hosts[0]._id : null;
  } catch {
    return null;
  }
}

/** memberToHost 맵 우선, API fallback */
export async function resolveMappingHostId(memberDeviceId) {
  const cached = getCachedMappingHostId(memberDeviceId);
  if (cached) return cached;
  const hostId = await fetchMappingHostIdFromApi(memberDeviceId);
  if (hostId != null) {
    memberToHostId.set(String(memberDeviceId), String(hostId));
  }
  return hostId;
}

/** 방 화면은 루트 스토어, changeDeviceState 는 home 모듈 컨텍스트로 들어온다. */
function commitPatchDevice(store, payload) {
  if (store?.state?.home?.zones) {
    store.commit('home/patchDeviceById', payload);
    return;
  }
  store.commit('patchDeviceById', payload);
}

export function patchMappingMemberOnVirtual(store, memberDeviceId, isOn) {
  if (!isOn) {
    clearDirectMemberOps([memberDeviceId]);
  }
  commitPatchDevice(store, {
    deviceId: memberDeviceId,
    data: isOn
      ? { onVirtual: true, item: { state: { onoff: 0 } } }
      // 호스트 OFF: 물리 소등 → UI도 OFF. bright/color(저장 설정)는 patch merge로 유지
      : { onVirtual: false, item: { onoff: 0, state: { onoff: 0 } } },
  });
}

function applyMemberHostLook(store, member, onoff) {
  const isOn = Number(onoff) === 1;
  if (member?.showState === true) {
    if (!isOn) clearDirectMemberOps([member._id]);
    commitPatchDevice(store, {
      deviceId: member._id,
      data: {
        onVirtual: false,
        item: isOn
          ? { onoff: 1, state: { onoff: 1 } }
          : { onoff: 0, state: { onoff: 0 } },
      },
    });
    return;
  }
  patchMappingMemberOnVirtual(store, member._id, isOn);
}

function applyMembersOnVirtual(store, members, onoff) {
  for (const member of members) {
    applyMemberHostLook(store, member, onoff);
  }
}

/** 매핑 호스트 조작 직전: fetch 후 멤버 UI 고정 + 호스트 op 기록 */
export async function onPhysicalMappingHostControl(store, hostDeviceId, onoff, bright, color) {
  recordPhysicalMappingHostOp(hostDeviceId, onoff, bright, color);

  const items = await fetchPhysicalMappingItems(hostDeviceId);
  const members = items.filter((it) => Number(it?.type) === DeviceType.Lamp && it._id != null);
  const memberIds = members.map((it) => it._id);
  const showStateById = new Map(members.map((it) => [String(it._id), it.showState === true]));
  rememberMappingGroup(hostDeviceId, memberIds, showStateById);
  clearDirectMemberOps(memberIds);
  applyMembersOnVirtual(store, members, onoff);
}

/** 매핑 멤버 직접 조작 직전: direct op 기록 + onVirtual 해제 (동기) */
export function preparePhysicalMappingMemberDirectControl(store, memberDeviceId, onoff, bright, color) {
  recordDirectMemberControl(memberDeviceId, onoff, bright, color);
  commitPatchDevice(store, {
    deviceId: memberDeviceId,
    data: {
      onVirtual: false,
      item: {
        onoff: Number(onoff) === 1 ? 1 : 0,
        state: { onoff: Number(onoff) === 1 ? 1 : 0 },
        bright: bright != null ? Number(bright) : 0,
        color: color != null ? Number(color) : 0,
      },
    },
  });
}
